/**
 * The consents that authorise CRM prospecting, named in one place so the client, the server and the
 * integrations service cannot disagree about which consent is which.
 *
 * `CrmConnection` is the `ConsentRecords.resourceType` these are recorded under. Consent is per CRM,
 * because an advisor may connect one and not another, and the provider is kept in `metadata.provider`:
 * `resourceId` is a UUID column meant for a row this consent is about, and a CRM is not a row.
 *
 * The wording lives in the `Consents` catalogue rather than here, so it can be revised without a
 * deploy. Only the identifiers and the version an advisor must have accepted live in code; bumping
 * a version here is what forces re-consent.
 *
 * The integrations service cannot import this package, so it keeps its own copy of these ids. If
 * you change one, change it there too — `integrations/src/prospecting/consent.ts` says the same.
 */
export const CRM_CONSENT_RESOURCE_TYPE = 'CrmConnection';

export const crmConsentDefs = {
  /** Connecting a CRM at all, and the note Waterlily writes back into it. */
  connection: {
    consentId: 'advisorCrmConnectionConsent',
    version: '1.0',
  },
  /** Reading and caching the advisor's entire contact list for prospecting. */
  bookRead: {
    consentId: 'advisorBookReadConsent',
    version: '1.0',
  },
} as const;

export type CrmConsentKind = keyof typeof crmConsentDefs;

export const CRM_CONSENT_IDS = Object.values(crmConsentDefs).map(def => def.consentId);

/**
 * Note visibility differs per CRM, so the sentence an advisor is shown does too. Captured verbatim
 * in the consent record, because "what did they actually agree to" cannot be reconstructed from an
 * id alone.
 */
export const CRM_NOTE_VISIBILITY_DISCLOSURE: Record<string, string> = {
  salesforce:
    'Notes Waterlily writes into Salesforce are visible to everyone in your Salesforce organisation.',
  wealthbox: 'Notes Waterlily writes into Wealthbox are private to you.',
  redtail: 'Waterlily records its summary against the contact in Redtail.',
};

/**
 * The CRMs a consent can be recorded for. A consent saved under any other name would be stored and
 * then never found by a lookup, so a name outside this list is refused where consent is saved.
 */
export const CRM_PROVIDERS = ['salesforce', 'redtail', 'wealthbox'] as const;

export type CrmProvider = (typeof CRM_PROVIDERS)[number];

export function isCrmProvider(value: unknown): value is CrmProvider {
  return typeof value === 'string' && (CRM_PROVIDERS as readonly string[]).includes(value);
}

/**
 * What the app answers about one CRM's consents: a decision per consent, not the records behind it.
 *
 * The one declaration of this shape. The server builds it (`fetchAdvisorCrmConsentsByAdvisorId`,
 * and its internal twin for the integrations service) and the client reads it. The integrations
 * service cannot import this package, so it declares the three fields it reads for itself
 * (`integrations/src/prospecting/consent.ts`); a field renamed here has to be renamed there.
 */
export interface CrmConsentState {
  provider: CrmProvider;
  /** Connecting the CRM, and the note Waterlily writes back. */
  hasConnectionConsent: boolean;
  /** Reading and caching the whole book. */
  hasBookReadConsent: boolean;
  connectionConsentedAt: string | null;
  bookReadConsentedAt: string | null;
}
